import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpService} from '../../../common/api/http.service';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {APIResponse, Filter} from '@core/interfaces/system/system-common';
import {LatestModelStatus, LiveModelRequest} from '@core/interfaces/live/live';
import {HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {
    BatteryBankCellMetricRequest,
    CellMetricTimeseriesChart,
    CellMetricAggregatedChart,
    BatteryBankCellStatusDto,
    BatteryBankMetricChartDto,
    BatteryBankModelMetricRequest,
    BatteryBankUnits,
    CellInfoDto,
    BatteryBankMetricSummary,
    BatteryBankChartTypes,
} from '@core/interfaces/live/battery-bank';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';

@Injectable()
export class BatteryBankApi {
    private readonly PREFIX = 'live/output/battery-bank';

    constructor(private api: HttpService) {}

    // Generic
    public getUnits(): Observable<BatteryBankUnits> {
        return this.api.get(`${this.PREFIX}/units`).pipe(map((res: APIResponse<BatteryBankUnits>) => res.response));
    }

    public getDatasource(id: string, filters: Filter[]): Observable<DataSource> {
        return this.api.postServerDataSource(`${this.PREFIX}/list?filterByassetId=${id}`, filters);
    }

    public getCountResults(assetId: string, uploadTime?: string): Observable<number> {
        return this.api
            .get(`${this.PREFIX}/count`, {
                params: new HttpParams().set('assetId', assetId || '').set('asOf', uploadTime || ''),
            })
            .pipe(map((res: APIResponse<number>) => res.response));
    }

    public getLatestStatusDetails(request: LiveModelRequest): Observable<LatestModelStatus> {
        return this.api
            .get(`${this.PREFIX}/latest`, {
                params: new HttpParams()
                    .set('assetId', request.assetId || '')
                    .set('model', request.model || '')
                    .set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<LatestModelStatus>) => res.response));
    }

    // Charts > Battery banks
    public getBatteryBankMetricsLatest(request: LiveModelRequest): Observable<BatteryBankMetricSummary> {
        return this.api
            .get(`${this.PREFIX}/data/bank/metrics-latest`, {
                params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<BatteryBankMetricSummary>) => res.response));
    }

    public getBatteryBankMetricByTime(request: BatteryBankModelMetricRequest): Observable<BatteryBankMetricChartDto> {
        return this.api
            .get(`${this.PREFIX}/data/bank/metrics/${request.chartType}/timeseries`, {
                params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<BatteryBankMetricChartDto>) => res.response));
    }

    // Charts > Cells
    public getCellsLatestStatus(request: LiveModelRequest): Observable<BatteryBankCellStatusDto> {
        return this.api
            .get(`${this.PREFIX}/data/cells/status-latest`, {
                params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<BatteryBankCellStatusDto>) => res.response));
    }

    public getCellsLatestStatusCsv(request: LiveModelRequest): Observable<DownloadCSVUrl> {
        return this.api.get(`${this.PREFIX}/data/cells/status-latest/csv`, {
            params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
        });
    }

    public getCellsAggregatedChart(
        request: LiveModelRequest,
        chartType: BatteryBankChartTypes,
    ): Observable<CellMetricAggregatedChart> {
        return this.api
            .get(`${this.PREFIX}/data/cells/metrics/${chartType}`, {
                params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<CellMetricAggregatedChart>) => res.response));
    }

    public getCellMetricChartByTime(request: BatteryBankCellMetricRequest): Observable<CellMetricTimeseriesChart> {
        return this.api
            .get(`${this.PREFIX}/data/cells/${request.cellId}/metrics/${request.chartType}/timeseries`, {
                params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<CellMetricTimeseriesChart>) => res.response));
    }

    // Cells
    public getCellOptions(request: LiveModelRequest): Observable<CellInfoDto[]> {
        return this.api
            .get(`${this.PREFIX}/cells/options`, {
                params: new HttpParams().set('assetId', request.assetId || '').set('asOf', request.uploadTime || ''),
            })
            .pipe(map((res: APIResponse<CellInfoDto[]>) => res.response));
    }

    public getCellResultList(request: LiveModelRequest, filterParams: Filter[]): Observable<DataSource> {
        const paramsString = `assetId=${request.assetId || ''}&asOf=${request.uploadTime || ''}`;

        return this.api.postServerDataSource(`${this.PREFIX}/cells/list?${paramsString}`, {filterParams});
    }

    public getCellResultListCsv(
        request: LiveModelRequest,
        paramsString: string,
        filterParams: Filter[],
    ): Observable<DownloadCSVUrl> {
        return this.api
            .post(
                `${this.PREFIX}/cells/list/csv${paramsString && '?' + paramsString}`,
                {filterParams},
                {
                    params: new HttpParams()
                        .set('assetId', request.assetId || '')
                        .set('asOf', request.uploadTime || ''),
                },
            )
            .pipe(map((res: APIResponse<DownloadCSVUrl>) => res.response));
    }
}
